

import { Component, Mixins, Watch } from 'vue-property-decorator';
import PopupRollout from '~/components/PopupRollout.vue';
import EditableItem from '~/components/RemoteFeatureFlags/Flags/EditableItem.vue';
import EditionButtons from '~/components/RemoteFeatureFlags/Flags/EditionButtons.vue';
import ReleaseButtons from '~/components/RemoteFeatureFlags/Flags/ReleaseButtons.vue';
import { LocalUserFlag, UserFlag } from '~/interfaces/user.interface';
import UserRightsMixin from '~/mixins/UserRightsMixin';
import { RemoteFeatureFlagsModule } from '~/store/modules/remoteFeatureFlags';
import DarkThemeInfoMixin from '~common-mixins/DarkThemeInfoMixin';

@Component({
    components: {
        EditableItem,
        EditionButtons,
        ReleaseButtons,
        PopupRollout
    }
})
export default class RemoteFeatureFlagsList extends Mixins(DarkThemeInfoMixin, UserRightsMixin) {
    localFeatureFlags: LocalUserFlag[] = [];
    sortBy: string = 'expiration';
    sortOptions = [
        { text: 'Expiration date', value: 'expiration' },
        { text: 'Creation date (oldest first)', value: 'creation' }
    ];

    @Watch('featureFlags', { deep: true, immediate: true })
    handler(featureFlags: UserFlag[]) {
        this.localFeatureFlags = this.sortFlags(featureFlags.map(this.defaultLocalFlagValue));
    }

    @Watch('sortBy')
    onSortByChange() {
        this.localFeatureFlags = this.sortFlags(this.localFeatureFlags);
    }

    sortFlags(flags: LocalUserFlag[]): LocalUserFlag[] {
        return this.sortBy === 'creation' ? this.sortByCreationDate(flags) : this.sortByExpirationDate(flags);
    }

    sortByExpirationDate(flags: LocalUserFlag[]): LocalUserFlag[] {
        return [...flags].sort((a, b) => {
            const firstDate = new Date(a.expire_at).getTime();
            const secondDate = new Date(b.expire_at).getTime();
            const firstValue = Number.isNaN(firstDate) ? Number.POSITIVE_INFINITY : firstDate;
            const secondValue = Number.isNaN(secondDate) ? Number.POSITIVE_INFINITY : secondDate;

            return firstValue - secondValue;
        });
    }

    sortByCreationDate(flags: LocalUserFlag[]): LocalUserFlag[] {
        return [...flags].sort((a, b) => a.created_at - b.created_at);
    }

    defaultLocalFlagValue(flag: UserFlag): LocalUserFlag {
        return {
            ...flag,
            expire_at: new Date(flag.expire_at).toISOString().substr(0, 10),
            isValid: false,
            isEditable: false
        };
    }

    updateFlagValue(id: string, key: string, value: string | boolean | number) {
        const flagIndex = this.localFeatureFlags.findIndex((flag) => flag.id === id);
        if (flagIndex !== -1) {
            (this.localFeatureFlags[flagIndex] as any)[key] = value;
        }
    }

    clearEdition(id: string) {
        const localFlag = this.localFeatureFlags.find((flag) => flag.id === id);
        const flag = this.featureFlags.find((flag) => flag.id === id);

        if (localFlag && flag) {
            const defaultFlag = this.defaultLocalFlagValue(flag);
            localFlag.description = defaultFlag.description;
            localFlag.expire_at = defaultFlag.expire_at;
            localFlag.isValid = defaultFlag.isValid;
            localFlag.isEditable = defaultFlag.isEditable;
        }
    }

    async updateFlag(flag: LocalUserFlag) {
        try {
            await RemoteFeatureFlagsModule.updateFlag({ name: flag.name, flag_id: flag.id, description: flag.description, expire_at: flag.expire_at });
            this.updateFlagValue(flag.id, 'isEditable', false);
        } catch (error) {
            console.error('Error while updating the feature flag', error);
            throw error;
        }
    }

    async openRolloutModal(flag: LocalUserFlag) {
        RemoteFeatureFlagsModule.openRolloutModal(flag);
    }

    async revertFlag(flag: LocalUserFlag) {
        try {
            await RemoteFeatureFlagsModule.revertFlag(flag.id);
            this.updateFlagValue(flag.id, 'released', false);
            this.updateFlagValue(flag.id, 'progress', 0);
        } catch (error) {
            console.error('Error while updating the feature flag', error);
        }
    }

    async deleteFlag(id: string) {
        if (confirm('Are you sure you want to delete this feature flag? This action cannot be undone.')) {
            try {
                await RemoteFeatureFlagsModule.deleteFlag(id);
                this.localFeatureFlags = this.localFeatureFlags.filter((flag) => flag.id !== id);
            } catch (error) {
                console.error('Error while deleting the feature flag', error);
            }
        }
    }

    flagProgression(flag: UserFlag): number {
        //usefull for old feature flags without progressive value
        if (!flag.progress && flag.released) {
            return 100;
        }
        return flag.progress || 0;
    }

    catchUpdateFlagValues(payload: { id: string; values: { key: string; value: boolean | number }[] }) {
        payload.values.map(({ key, value }) => {
            this.updateFlagValue(payload.id, key, value);
        });
    }

    get featureFlags() {
        return RemoteFeatureFlagsModule.availableFlags;
    }

}
